import { useEffect, useState } from 'react';
import { useNavigate } from '@usas/ui-web/wrappers/ReactRouterDom';

import AthleteUserConstants from '../AthleteUserConstants';
import NavLinks from '../../../meet/NavLinks';
import EntryNavLinks from '../../EntryNavLinks';
import MyMeetsNavLinks from '../../../myMeets/MyMeetsNavLinks';
import MyAthleteEntryNavLinks from '../../../myAthleteEntry/MyAthleteEntryNavLinks';

import useOmeMeetData from '../../../../state/omeMeet/UseOmeMeetData';
import useOrgUnitAthleteEntryInfoData from '../../../../state/orgUnitAthleteEntryInfo/UseOrgUnitAthleteEntryInfoData';
import useOmeMeetOrgUnitAthleteEntryIndividualRosterEntryData from '../../../../state/omeMeetOrgUnitAthleteEntryIndividualRosterEntry/UseOmeMeetOrgUnitAthleteEntryIndividualRosterEntryData';
import useAthleteMeetEventData from '../../../../state/athleteMeetEvent/UseAthleteMeetEventData';
import useAthleteEntryContextData from '../../../../state/athleteEntryContextView/UseAthleteEntryContextData';
import useOmeMeetOrgUnitEntryTimesData from '../../../../state/omeMeetOrgUnitEntryTimes/UseOmeMeetOrgUnitEntryTimesData';
import useMyAthleteMeetEntriesFiltersData from '../../../../state/myAthleteMeetEntriesFilters/UseMyAthleteMeetEntriesFiltersData';
import useOrgUnitAthleteEntryIndividualRosterEntryData from '../../../../state/orgUnitAthleteEntry/UseOrgUnitAthleteEntryIndividualRosterEntryData';

import useEnvironmentVariableData from '@usas/ui-web/state/environmentVariable/UseEnvironmentVariableData';
import useSearchMeetData from '@usas/ui-web/state/searchMeet/UseSearchMeetData';
import useSelectOrgUnitData from '@usas/ui-web/state/selectOrgUnit/UseSelectOrgUnitData';
import useLeftNavModalData from '@usas/ui-web/state/leftNavModal/UseLeftNavModalData';

import Constants from '@usas/ui-web/utils/Constants';
import { formatTimeForDisplay } from '@usas/ui-web/utils/TimesUtils';
import { rosterEntriesIsComplete } from '../../../../components/leftNavigationOmeAthleteEntry/LeftNavValidation';

const INITIAL_MODAL_STATE = {
  displayPopUp: false,
  modalTitle: 'Delete Individual Event Entry?',
  athlete: '',
  eventName: '',
  eventCompetitionGenderTypeName: '',
  ageGroupName: '',
  meetSessionName: '',
  entryTime: '',
  timeEvent: '',
  qualifyingCourseCode: '',
  qualifiedAsBonus: '',
  override: '',
  status: '',
  individualAthleteMeetEventId: Constants.DEFAULT_ID
};

const useAthleteUserEntries = () => {
  const navigate = useNavigate();
  const { omeMeetState } = useOmeMeetData();
  const { orgUnitAthleteEntryInfoState, getOrgUnitAthleteEntryInfo } = useOrgUnitAthleteEntryInfoData();
  const { omeMeetOrgUnitAthleteEntryIndividualRosterEntryState, getOrgUnitAthleteEntryIndividualRosterEntryByOrgUnitAthleteEntryId } =
    useOmeMeetOrgUnitAthleteEntryIndividualRosterEntryData();
  const { orgUnitAthleteEntryIndividualRosterEntryState, getOrgUnitAthleteEntryIndividualRosterEntry } = useOrgUnitAthleteEntryIndividualRosterEntryData();
  const { athleteMeetEventState, deleteIndividualAthleteMeetEvent } = useAthleteMeetEventData();
  const { omeMeetOrgUnitEntryTimesState, putOmeMeetOrgUnitEntryTimes, confirmSaveOmeMeetOrgUnitEntryTimes } = useOmeMeetOrgUnitEntryTimesData();
  const { ATHLETE_INDIVIDUAL_EVENT_ENTRY_OME_FEE_TYPE_ID, ATHLETE_INDIVIDUAL_BONUS_EVENT_ENTRY_OME_FEE_TYPE_ID, HIDE_OME_ELIGIBILITY_50_BONUS_OPTION } = useEnvironmentVariableData();
  const { leftNavModalState, setLeftNavModalState } = useLeftNavModalData();
  const [state, setState] = useState({ tryGetAfterUpdateOrDelete: false, tryUpdateGridData: false, bonusEvents: '' });
  const [gridState, setGridState] = useState({ gridData: [], showUpdateButton: false });
  const [modalState, setModalState] = useState(INITIAL_MODAL_STATE);
  const [errorState, setErrorState] = useState({ general: '' });
  const { searchMeetState, setSearchMeetState } = useSearchMeetData();
  const { selectOrgUnitState } = useSelectOrgUnitData();
  const { athleteEntryContextState } = useAthleteEntryContextData();
  const { myAthleteMeetEntriesFiltersState } = useMyAthleteMeetEntriesFiltersData();

  const onAddEventClicked = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    if (omeMeetState.route === '') {
      //OME Route
      navigate(EntryNavLinks.ATHLETE_ENTRIES_INDIVIDUAL_SELECTION);
    }
    else {
      //Meet Route
      navigate(NavLinks.ATHLETE_ENTRIES_INDIVIDUAL_SELECTION);
    }
  }

  const onAddBonusEventClicked = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    if (omeMeetState.route === '') {
      //OME Route
      navigate(EntryNavLinks.ATHLETE_ENTRIES_BONUS_INDIVIDUAL_SELECTION);
    }
    else {
      //Meet Route
      navigate(NavLinks.ATHLETE_ENTRIES_BONUS_INDIVIDUAL_SELECTION);
    }
  }

  const onDeleteEventClicked = (e, individualAthleteMeetEvent) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    let bonusRuleViolated = false;
    let bonusRuleViolatedErrorMsg = '';
    let updatedAthleteEventsToExcludeArray = [];
    let updatedAthleteEventsToExcludeArrayAllOmeMeetEntry = [];
    //if bonus events are allowed and the selected event is not a bonus event, check to see if the delete would violate any bonus rules
    if (state.bonusEvents === true && individualAthleteMeetEvent.qualifiedAsBonus === false) {
      const eventsAfterTheDelete = JSON.parse(JSON.stringify(omeMeetOrgUnitAthleteEntryIndividualRosterEntryState.objData?.events));
      const selectedIndex = eventsAfterTheDelete.findIndex(x => x.individualAthleteMeetEventId === individualAthleteMeetEvent.individualAthleteMeetEventId);
      if (selectedIndex > -1) {
        eventsAfterTheDelete.splice(selectedIndex, 1);
      }

      //figure out the number of individual and bonus entries after the potential delete
      let updatedAthleteIndividualEventCount = 0;
      let updatedAthleteIndividualEventCountAllOmeMeetEntry = 0;
      let updatedAthleteBonusIndividualEventCount = 0;
      let updatedAthleteBonusIndividualEventCountAllOmeMeetEntry = 0;
      if (eventsAfterTheDelete) {
        for (let i = 0; i < eventsAfterTheDelete.length; i++) {
          if (eventsAfterTheDelete[i].qualifiedAsBonus === false) {
            updatedAthleteIndividualEventCount += 1;
          }
          else if (eventsAfterTheDelete[i].qualifiedAsBonus === true) {
            if (HIDE_OME_ELIGIBILITY_50_BONUS_OPTION !== 1) {
              if (eventsAfterTheDelete[i].excludeFromTotal === false) {
                updatedAthleteBonusIndividualEventCount += 1;
              }
              else {
                updatedAthleteEventsToExcludeArray.push(eventsAfterTheDelete[i]);
              }
            }
            else {
              updatedAthleteBonusIndividualEventCount += 1;
            }
          }
        }
      }

      updatedAthleteIndividualEventCountAllOmeMeetEntry = updatedAthleteIndividualEventCount + orgUnitAthleteEntryIndividualRosterEntryState.arrayData.athleteDiffOmeMeetEntryIndividualEventsAdded;
      updatedAthleteBonusIndividualEventCountAllOmeMeetEntry = updatedAthleteBonusIndividualEventCount + orgUnitAthleteEntryIndividualRosterEntryState.arrayData.athleteDiffOmeMeetEntryBonusEventsAdded - orgUnitAthleteEntryIndividualRosterEntryState.arrayData.athleteDiffOmeMeetEntryExcludedBonusEventsAdded;
      updatedAthleteEventsToExcludeArrayAllOmeMeetEntry = orgUnitAthleteEntryIndividualRosterEntryState.arrayData.athleteDiffOmeMeetEntryExcludedBonusEvents !== null ? updatedAthleteEventsToExcludeArray.concat(orgUnitAthleteEntryIndividualRosterEntryState.arrayData.athleteDiffOmeMeetEntryExcludedBonusEvents) : updatedAthleteEventsToExcludeArray;

      //check to see if any bonus rules are violated by the potential delete
      let differenceBetweenBonusEntriesAndAllowedBonusEntriesAllOmeMeetEntry = 0;
      for (let i = 0; i < omeMeetState.objData.omeMeetQualification[0]?.omeQualificationBonus.length; i++) {
        //find the bonus rule that would apply to the number of individual (non-bonus) event entries after the delete
        if (updatedAthleteIndividualEventCountAllOmeMeetEntry <= omeMeetState.objData.omeMeetQualification[0]?.omeQualificationBonus[i].maxQualifyingEvents &&
          updatedAthleteIndividualEventCountAllOmeMeetEntry >= omeMeetState.objData.omeMeetQualification[0]?.omeQualificationBonus[i].minQualifyingEvents) {
          if (updatedAthleteBonusIndividualEventCountAllOmeMeetEntry > omeMeetState.objData.omeMeetQualification[0]?.omeQualificationBonus[i].bonusStandardCount) {
            bonusRuleViolated = true;
            differenceBetweenBonusEntriesAndAllowedBonusEntriesAllOmeMeetEntry = updatedAthleteBonusIndividualEventCountAllOmeMeetEntry - omeMeetState.objData.omeMeetQualification[0]?.omeQualificationBonus[i].bonusStandardCount;

            if (HIDE_OME_ELIGIBILITY_50_BONUS_OPTION !== 1) {
              bonusRuleViolatedErrorMsg = `Deleting this individual event entry would leave this athlete with ${updatedAthleteIndividualEventCountAllOmeMeetEntry} individual event ${updatedAthleteIndividualEventCountAllOmeMeetEntry === 1 ? 'entry' : 'entries'}. Based on the bonus event rules of the meet, an athlete with ${updatedAthleteIndividualEventCount} individual event ${updatedAthleteIndividualEventCountAllOmeMeetEntry === 1 ? 'entry' : 'entries'} is allowed ${omeMeetState.objData.omeMeetQualification[0]?.omeQualificationBonus[i].bonusStandardCount} individual bonus ${omeMeetState.objData.omeMeetQualification[0]?.omeQualificationBonus[i].bonusStandardCount === 1 ? 'event entry' : 'event entries'}. This athlete has ${updatedAthleteBonusIndividualEventCountAllOmeMeetEntry} individual bonus event ${updatedAthleteBonusIndividualEventCountAllOmeMeetEntry === 1 ? 'entry' : 'entries'}${updatedAthleteEventsToExcludeArrayAllOmeMeetEntry.length > 0 ? ` (${updatedAthleteEventsToExcludeArrayAllOmeMeetEntry.map(event => event.eventName).join(', ')} are excluded from the allowed individual bonus ${omeMeetState.objData.omeMeetQualification[0]?.omeQualificationBonus[i].bonusStandardCount === 1 ? 'event entry' : 'event entries'} count)` : ''}, which would not be allowed.\n\nIn order to be able to delete this individual event entry, please delete ${differenceBetweenBonusEntriesAndAllowedBonusEntriesAllOmeMeetEntry} individual bonus event ${differenceBetweenBonusEntriesAndAllowedBonusEntriesAllOmeMeetEntry === 1 ? 'entry that is' : 'entries that are'} included in the allowed individual bonus event entries count.`
            }
            else {
              bonusRuleViolatedErrorMsg = `Deleting this individual event entry would leave this athlete with ${updatedAthleteIndividualEventCountAllOmeMeetEntry} individual event ${updatedAthleteIndividualEventCountAllOmeMeetEntry === 1 ? 'entry' : 'entries'}. Based on the bonus event rules of the meet, an athlete with ${updatedAthleteIndividualEventCount} individual event ${updatedAthleteIndividualEventCountAllOmeMeetEntry === 1 ? 'entry' : 'entries'} is allowed ${omeMeetState.objData.omeMeetQualification[0]?.omeQualificationBonus[i].bonusStandardCount} individual bonus ${omeMeetState.objData.omeMeetQualification[0]?.omeQualificationBonus[i].bonusStandardCount === 1 ? 'event entry' : 'event entries'}. This athlete has ${updatedAthleteBonusIndividualEventCountAllOmeMeetEntry} individual bonus event ${updatedAthleteBonusIndividualEventCountAllOmeMeetEntry === 1 ? 'entry' : 'entries'}, which would not be allowed.\n\nIn order to be able to delete this individual event entry, please delete ${differenceBetweenBonusEntriesAndAllowedBonusEntriesAllOmeMeetEntry} individual bonus event ${differenceBetweenBonusEntriesAndAllowedBonusEntriesAllOmeMeetEntry === 1 ? 'entry' : 'entries'}.`
            }
          }
        }
      }
    }

    //if there is an error, don't show the delete pop-up
    if (bonusRuleViolated === true) {
      setErrorState({ ...errorState, general: bonusRuleViolatedErrorMsg });
    }
    else {
      setErrorState({ ...errorState, general: '' });
      setModalState({
        ...modalState,
        displayPopUp: true,
        athlete: `${(omeMeetOrgUnitAthleteEntryIndividualRosterEntryState?.objData?.firstName || omeMeetOrgUnitAthleteEntryIndividualRosterEntryState?.objData?.middleName || omeMeetOrgUnitAthleteEntryIndividualRosterEntryState?.objData?.lastName) ? `${omeMeetOrgUnitAthleteEntryIndividualRosterEntryState?.objData?.firstName || ''} ${omeMeetOrgUnitAthleteEntryIndividualRosterEntryState?.objData?.preferredName !== '' && omeMeetOrgUnitAthleteEntryIndividualRosterEntryState?.objData?.preferredName !== omeMeetOrgUnitAthleteEntryIndividualRosterEntryState?.objData?.firstName ? '"' + omeMeetOrgUnitAthleteEntryIndividualRosterEntryState?.objData?.preferredName + '"' : ''} ${omeMeetOrgUnitAthleteEntryIndividualRosterEntryState?.objData?.middleName || ''} ${omeMeetOrgUnitAthleteEntryIndividualRosterEntryState?.objData?.lastName || ''}` : ''} (${omeMeetOrgUnitAthleteEntryIndividualRosterEntryState?.objData?.competitionGenderTypeName || ''}, ${omeMeetOrgUnitAthleteEntryIndividualRosterEntryState?.objData?.ageAtMeet >= 0 ? omeMeetOrgUnitAthleteEntryIndividualRosterEntryState?.objData?.ageAtMeet : ''})`,
        eventName: individualAthleteMeetEvent.eventName || '',
        eventCompetitionGenderTypeName: individualAthleteMeetEvent.eventCompetitionGenderTypeName || '',
        ageGroupName: individualAthleteMeetEvent.ageGroupName || '',
        meetSessionName: individualAthleteMeetEvent.meetSessionName || '',
        entryTime: individualAthleteMeetEvent.entryTime ? formatTimeForDisplay(individualAthleteMeetEvent.entryTime) : 'NT',
        timeEvent: `${individualAthleteMeetEvent.isAlternateEvent === true ? `ALT: ${individualAthleteMeetEvent.qualifyingEventName || ''}` : individualAthleteMeetEvent.isNonConformingTime === true ? `NC: ${individualAthleteMeetEvent.qualifyingEventName || ''}` : `${individualAthleteMeetEvent.qualifyingEventName || ''}`}`,
        qualifyingCourseCode: individualAthleteMeetEvent.qualifyingCourseCode || '',
        qualifiedAsBonus: individualAthleteMeetEvent.qualifiedAsBonus === true ? 'Yes' : individualAthleteMeetEvent.qualifiedAsBonus === false ? 'No' : '',
        override: individualAthleteMeetEvent.swimTimeEntryOverride === true ? 'Yes' : individualAthleteMeetEvent.swimTimeEntryOverride === false ? 'No' : '',
        status: individualAthleteMeetEvent.status || '',
        individualAthleteMeetEventId: individualAthleteMeetEvent.individualAthleteMeetEventId || Constants.DEFAULT_ID
      });
    }
  };

  const onDeleteEvent = (e, individualAthleteMeetEventId) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    deleteIndividualAthleteMeetEvent(individualAthleteMeetEventId);
    onModalCanceled();
    setState({ ...state, tryGetAfterUpdateOrDelete: true });
  };

  const onModalCanceled = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    setModalState(INITIAL_MODAL_STATE);
  };

  const onUpdateAthleteTimes = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    putOmeMeetOrgUnitEntryTimes(orgUnitAthleteEntryInfoState.objData.omeMeetOrgUnitEntryId, omeMeetOrgUnitAthleteEntryIndividualRosterEntryState.objData.orgUnitAthleteEntryId);
    setState({ ...state, tryGetAfterUpdateOrDelete: true });
  };

  const onNextButtonClicked = (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    setState({ ...state, submitButton: AthleteUserConstants.CONTINUE, tryRedirect: true });
    setErrorState({ ...errorState, general: '' });
  };

  const onBackButtonClicked = (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    setState({ ...state, submitButton: AthleteUserConstants.BACK, tryRedirect: true, error: '' });
  };

  const onSaveAndExitButtonClicked = (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    setState({ ...state, submitButton: AthleteUserConstants.SAVE_AND_EXIT, tryRedirect: true });
    setErrorState({ ...errorState, general: '' });
  };

  useEffect(() => {
    setLeftNavModalState({ ...leftNavModalState, formStateSaved: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (omeMeetOrgUnitAthleteEntryIndividualRosterEntryState?.objData?.orgUnitAthleteEntryId) {
      getOrgUnitAthleteEntryIndividualRosterEntry(omeMeetOrgUnitAthleteEntryIndividualRosterEntryState.objData?.orgUnitAthleteEntryId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [omeMeetOrgUnitAthleteEntryIndividualRosterEntryState?.objData?.orgUnitAthleteEntryId])

  useEffect(() => {
    if (omeMeetOrgUnitAthleteEntryIndividualRosterEntryState.isSaved === true) {
      confirmSaveOmeMeetOrgUnitEntryTimes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [omeMeetOrgUnitAthleteEntryIndividualRosterEntryState.isSaved])

  useEffect(() => {
    if (omeMeetOrgUnitAthleteEntryIndividualRosterEntryState.isObjLoaded === true &&
      Object.keys(omeMeetOrgUnitAthleteEntryIndividualRosterEntryState.objData).length > 0) {
      setState({ ...state, bonusEvents: orgUnitAthleteEntryInfoState.objData?.bonusEvents ?? '' });

      let showUpdateButton = false;
      if (omeMeetState.objData?.omeMeetPrice?.length > 0) {
        const hasCostPerAthleteIndividualEventEntry = omeMeetState.objData?.omeMeetPrice.find(x => x.omeFeeTypeId === ATHLETE_INDIVIDUAL_EVENT_ENTRY_OME_FEE_TYPE_ID)?.amount !== (null || undefined) ? true : false;
        const hasCostPerAthleteIndividualBonusEventEntry = omeMeetState.objData?.omeMeetPrice.find(x => x.omeFeeTypeId === ATHLETE_INDIVIDUAL_BONUS_EVENT_ENTRY_OME_FEE_TYPE_ID)?.amount !== (null || undefined) ? true : false;
        if (hasCostPerAthleteIndividualEventEntry === true && hasCostPerAthleteIndividualBonusEventEntry === true) {
          //if Individual Event Cost equals Bonus Event Cost, then show the update times button
          if (omeMeetState.objData?.omeMeetPrice.find(x => x.omeFeeTypeId === ATHLETE_INDIVIDUAL_EVENT_ENTRY_OME_FEE_TYPE_ID)?.amount ===
            omeMeetState.objData?.omeMeetPrice.find(x => x.omeFeeTypeId === ATHLETE_INDIVIDUAL_BONUS_EVENT_ENTRY_OME_FEE_TYPE_ID)?.amount) {
            showUpdateButton = true;
          }
        }
        //if we don't have both Individual and Bonus Event prices for the meet, then show the update times button
        else {
          showUpdateButton = true;
        }
      }
      //if we don't have pricing for the meet, then show the update times button
      else {
        showUpdateButton = true;
      }

      setGridState({ ...gridState, gridData: omeMeetOrgUnitAthleteEntryIndividualRosterEntryState.objData?.events || [], showUpdateButton: showUpdateButton });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [omeMeetOrgUnitAthleteEntryIndividualRosterEntryState])

  useEffect(() => {
    if (omeMeetOrgUnitAthleteEntryIndividualRosterEntryState?.objData?.orgUnitAthleteEntryId &&
      omeMeetOrgUnitAthleteEntryIndividualRosterEntryState?.omeMeetOrgUnitEntryId &&
      state.tryGetAfterUpdateOrDelete === true && orgUnitAthleteEntryInfoState.isObjLoading === false &&
      athleteMeetEventState.isSaving === false && omeMeetOrgUnitEntryTimesState.isSaving === false) {
      getOrgUnitAthleteEntryIndividualRosterEntryByOrgUnitAthleteEntryId(
        omeMeetOrgUnitAthleteEntryIndividualRosterEntryState?.objData?.orgUnitAthleteEntryId,
        omeMeetOrgUnitAthleteEntryIndividualRosterEntryState?.omeMeetOrgUnitEntryId);
      //Get info after update or delete
      getOrgUnitAthleteEntryInfo(omeMeetOrgUnitAthleteEntryIndividualRosterEntryState?.objData?.orgUnitAthleteEntryId);
      getOrgUnitAthleteEntryIndividualRosterEntry(omeMeetOrgUnitAthleteEntryIndividualRosterEntryState.objData?.orgUnitAthleteEntryId);
      setState({ ...state, tryGetAfterUpdateOrDelete: false, tryUpdateGridData: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [omeMeetOrgUnitAthleteEntryIndividualRosterEntryState?.objData?.orgUnitAthleteEntryId,
  omeMeetOrgUnitAthleteEntryIndividualRosterEntryState?.objData?.omeMeetOrgUnitEntryId, state.tryGetAfterUpdateOrDelete,
  orgUnitAthleteEntryInfoState.isObjLoading, athleteMeetEventState.isSaving, omeMeetOrgUnitEntryTimesState.isSaving])

  useEffect(() => {
    if (state.tryUpdateGridData === true && omeMeetOrgUnitAthleteEntryIndividualRosterEntryState.isObjLoading === false) {
      const updatedAthlete = omeMeetOrgUnitAthleteEntryIndividualRosterEntryState.objData;
      setState({ ...state, tryUpdateGridData: false });

      let showUpdateButton = false;
      if (omeMeetState.objData?.omeMeetPrice?.length > 0) {
        const hasCostPerAthleteIndividualEventEntry = omeMeetState.objData?.omeMeetPrice.find(x => x.omeFeeTypeId === ATHLETE_INDIVIDUAL_EVENT_ENTRY_OME_FEE_TYPE_ID)?.amount !== (null || undefined) ? true : false;
        const hasCostPerAthleteIndividualBonusEventEntry = omeMeetState.objData?.omeMeetPrice.find(x => x.omeFeeTypeId === ATHLETE_INDIVIDUAL_BONUS_EVENT_ENTRY_OME_FEE_TYPE_ID)?.amount !== (null || undefined) ? true : false;
        if (hasCostPerAthleteIndividualEventEntry === true && hasCostPerAthleteIndividualBonusEventEntry === true) {
          //if Individual Event Cost equals Bonus Event Cost, then show the update times button
          if (omeMeetState.objData?.omeMeetPrice.find(x => x.omeFeeTypeId === ATHLETE_INDIVIDUAL_EVENT_ENTRY_OME_FEE_TYPE_ID)?.amount ===
            omeMeetState.objData?.omeMeetPrice.find(x => x.omeFeeTypeId === ATHLETE_INDIVIDUAL_BONUS_EVENT_ENTRY_OME_FEE_TYPE_ID)?.amount) {
            showUpdateButton = true;
          }
        }
        //if we don't have both Individual and Bonus Event prices for the meet, then show the update times button
        else {
          showUpdateButton = true;
        }
      }
      //if we don't have pricing for the meet, then show the update times button
      else {
        showUpdateButton = true;
      }

      setGridState({ ...gridState, gridData: updatedAthlete.events || [], showUpdateButton: showUpdateButton });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.tryUpdateGridData, omeMeetOrgUnitAthleteEntryIndividualRosterEntryState])

  useEffect(() => {
    if (athleteMeetEventState.isSaving === false && orgUnitAthleteEntryInfoState.isObjLoading === false && omeMeetOrgUnitAthleteEntryIndividualRosterEntryState.isArrayLoading === false &&
      omeMeetOrgUnitAthleteEntryIndividualRosterEntryState.isObjLoading === false && omeMeetOrgUnitEntryTimesState.isSaving === false &&
      state.tryRedirect === true) {
      if (state.submitButton === AthleteUserConstants.CONTINUE) {
        if (rosterEntriesIsComplete(orgUnitAthleteEntryInfoState.objData) === false) {
          setState({
            ...state, tryRedirect: false, submitButton: ''
          });
          setErrorState({ ...errorState, general: `Athlete must have a status of 'Events Approved'${orgUnitAthleteEntryInfoState.objData?.allowRelayOnlySwimmer === true ? " or 'Relay Only Swimmer'." : orgUnitAthleteEntryInfoState.objData?.allowRelayOnlySwimmer === false ? " ('Relay Only Swimmer' status is not allowed)." : '.'}` })
        }
        else {
          if (omeMeetState.route !== '') {
            navigate(NavLinks.ATHLETE_CREDENTIALS);
          }
          else {
            navigate(EntryNavLinks.ATHLETE_CREDENTIALS);
          }
        }
      }
      else if (state.submitButton === AthleteUserConstants.SAVE_AND_EXIT) {
        if (omeMeetState.route === '') {
          if (selectOrgUnitState.orgUnitId === '') {
            //My Athlete Meet Entries Route
            navigate(MyAthleteEntryNavLinks.MY_ATHLETE_ENTRY, { state: { myAthleteMeetEntriesFiltersState: myAthleteMeetEntriesFiltersState } });
          }
          else {
            //OME Route
            navigate(MyMeetsNavLinks.MEET_ENTRY_ATHLETE, { state: { selectOrgUnitState: selectOrgUnitState } });
          }
        }
        else {
          //Meet Route
          setSearchMeetState({ ...searchMeetState, showMeetHeader: true });

          navigate(NavLinks.MEET_ENTRY_ATHLETE, { state: { goToMeet: true } });
        }
      }
      else if (state.submitButton === AthleteUserConstants.BACK) {
        if (omeMeetState.route !== '') {
          navigate(NavLinks.ATHLETE_CONTACTS);
        }
        else {
          navigate(EntryNavLinks.ATHLETE_CONTACTS);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [athleteMeetEventState.isSaving, orgUnitAthleteEntryInfoState.isObjLoading, omeMeetOrgUnitAthleteEntryIndividualRosterEntryState.isArrayLoading,
  omeMeetOrgUnitAthleteEntryIndividualRosterEntryState.isObjLoading, omeMeetOrgUnitEntryTimesState.isSaving, state, orgUnitAthleteEntryInfoState.objData]);

  return {
    athleteEntryContextState,
    orgUnitAthleteEntryInfoState,
    omeMeetOrgUnitEntryTimesState,
    state,
    gridState,
    omeMeetOrgUnitAthleteEntryIndividualRosterEntryState,
    orgUnitAthleteEntryIndividualRosterEntryState,
    athleteMeetEventState,
    onAddEventClicked,
    onAddBonusEventClicked,
    onDeleteEventClicked,
    onDeleteEvent,
    onModalCanceled,
    modalState,
    errorState,
    onUpdateAthleteTimes,
    onNextButtonClicked,
    onBackButtonClicked,
    onSaveAndExitButtonClicked
  };
}

export default useAthleteUserEntries;